import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Second-Term Missionary Support Raising: What Changes the Second Time Around';
const DESCRIPTION =
    'Raising support for a second term is a different task than raising it the first time — a practical guide to re-engaging existing donors, explaining rising costs, and finding new supporters without starting from zero.';

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover30.jpg',
            datePublished: '2026-07-28',
            dateModified: '2026-07-28',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/second-term-missionary-support-raising',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Is it easier to raise support for a second term than the first?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Usually, but not automatically. You start with a track record and warm relationships instead of a cold list, which is a real advantage — but donor attrition between terms is normal, and re-engaging lapsed supporters still takes deliberate outreach, not just an assumption that everyone will re-commit.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How do I ask existing donors to increase their gift for a second term?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Lead with specific numbers, not a general appeal — your actual field costs today versus what they were when the donor first committed. Most long-term donors expect costs to rise over a multi-year gap and respond well to an honest, specific update rather than a vague request for 'more.'",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What percentage of first-term donors typically continue into a second term?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "It varies widely by relationship depth and how well you communicated during the first term, but expect meaningful attrition — commonly a quarter to a half of first-term donors lapse by the time a second term begins. Budget your second-term campaign assuming you'll need some new supporters, not just renewals.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I change my support letter for a second term?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes — a second-term letter should open with what happened during the first term, not repeat your original calling story from scratch. Donors who already know you want evidence of impact and a clear reason for the new ask, not a reintroduction.",
                    },
                },
            ],
        },
    ],
};

export default function SecondTermMissionarySupportRaising() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/second-term-missionary-support-raising' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-second-term-support-raising-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover30.jpg" alt="A well-worn suitcase and passport on a wooden table, representing a missionary preparing to return to the field for another term" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Raising support for a second term feels like it should be easier — you have a track record, real relationships, and proof the work matters. It usually is easier, but not automatically, and not in the same way. The task shifts from introducing yourself and your calling to re-engaging people who already know you, explaining why costs have changed, and rebuilding the portion of your donor base that naturally drifted away during your first term. Treat it as a distinct campaign with its own plan, not a rerun of your first support letter.</p>

                    <h2>Expect real attrition, and plan for it</h2>

                    <p>Even donors who genuinely believe in your work lapse between terms — life changes, priorities shift, some simply forget to update an expired card. It's common for a quarter to a half of first-term donors to not carry forward automatically. This isn't a reflection on your ministry; it's the normal shape of long-distance relationships over a multi-year gap. Build your second-term plan assuming you'll need to both re-engage a meaningful share of past donors and add new ones, rather than assuming your existing base will simply continue.</p>

                    <h2>Lead with what happened, not who you are</h2>

                    <p>Your first-term letter had to establish your calling and character from nothing. Your second-term letter doesn't — donors who supported you already know that story. Open instead with what actually happened: what you accomplished, what you learned, what changed on the ground. Evidence of impact is the strongest asset a returning missionary has and the one most second-term letters underuse by defaulting back to a generic calling narrative. See <a href="/resources/how-to-write-a-missionary-support-letter">how to write a support letter that gets funded</a> for the underlying structure, then adapt the opening specifically to lead with results.</p>

                    <h2>Be specific and honest about rising costs</h2>

                    <p>Field costs rarely stay flat across a multi-year term — currency shifts, inflation, housing, and changing family needs all push numbers up. Donors who committed to a monthly gift years ago are not necessarily aware their original amount no longer covers what it used to. State your actual current budget plainly, show what's changed since they first committed, and make a specific, honest ask rather than a vague appeal to "give more." Most long-term donors respond well to transparency about real numbers and respond poorly to a request that feels like it's dodging the reason behind it.</p>

                    <h2>Re-engage lapsed donors individually, not with a mass email</h2>

                    <p>Donors who quietly stopped giving during your first term deserve a different message than one going to your still-active supporters — a personal note acknowledging the gap, an honest update on what's changed, and a low-pressure invitation to reconnect rather than a direct re-ask. Many lapses were mechanical (an expired card, a missed update) rather than a decision to stop caring, and a warm individual outreach recovers a meaningful share of these relationships. This is also the natural moment to check whether they simply never received your updates in the first place.</p>

                    <h2>Use home assignment or furlough as the campaign engine</h2>

                    <p>If your second-term ask lines up with a home assignment, that in-person window is your highest-leverage opportunity — conversations convert better than letters, and you're already meeting people face to face. See the guide on <a href="/resources/missionary-furlough-support-raising">using furlough to reconnect with donors</a> for how to structure that season around your support campaign rather than letting travel logistics crowd it out.</p>

                    <h2>Don't skip the referral ask just because you're returning, not starting</h2>

                    <p>It's tempting to treat second-term fundraising purely as a renewal exercise and skip asking for new introductions. Don't — your most committed donors now have years of proof to point to when introducing you to someone new, which makes a second-term referral ask arguably stronger than a first-term one. Ask each committed donor for one or two names as part of your re-engagement conversation, not as a separate afterthought.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Is it easier to raise support for a second term than the first?</strong><br />
                    Usually, thanks to a track record and warm relationships — but attrition is normal, and re-engaging lapsed donors still takes deliberate outreach.</p>

                    <p><strong>How do I ask existing donors to increase their gift for a second term?</strong><br />
                    Lead with specific current numbers versus what they were originally, and make an honest, specific ask rather than a vague appeal.</p>

                    <p><strong>What percentage of first-term donors typically continue into a second term?</strong><br />
                    Expect meaningful attrition — commonly a quarter to a half lapse — so budget your campaign assuming you'll need some new supporters too.</p>

                    <p><strong>Should I change my support letter for a second term?</strong><br />
                    Yes — open with what happened during your first term and evidence of impact, not a reintroduction to your original calling story.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/missionary-furlough-support-raising">Missionary Furlough Support Raising: How to Reconnect With Donors During Home Assignment</a></li>
                        <li><a href="/resources/donor-retention-for-missionaries">Donor Retention for Missionaries: How to Stop Supporters From Quietly Lapsing</a></li>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter That Actually Gets You Funded</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built this after hearing the same surprise from second-term missionaries: they expected renewal to be automatic, and it almost never is without a deliberate re-engagement plan.</p>
                    <p><small><em>Last updated: 2026-07-28.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/second-term-missionary-support-raising" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
